import React from 'react';
import { Box, Group, Stack, Text } from '@mantine/core';
import {
    SCENE_TEMPLATES,
    SCREEN_HEIGHT_PX,
    SCREEN_WIDTH_PX,
    panelPositionLabels,
    panelSizeLabel,
    type SceneTemplateId,
} from '../../data/ShellConfig';

type SceneTemplateHintProps = {
    templateId: SceneTemplateId;
};

/**
 * Sizing guidance for the currently selected scene template: the pixel
 * dimensions each panel renders at, so artwork and embedded pages can be built
 * to fit instead of being letterboxed or cropped by the shell.
 *
 * Swatch colours match the template picker's thumbnails (blue/violet by index),
 * so a row here reads as the band directly above it.
 */
const SceneTemplateHint: React.FC<SceneTemplateHintProps> = ({ templateId }) => {
    const template = SCENE_TEMPLATES.find(t => t.id === templateId) ?? SCENE_TEMPLATES[0];
    const positions = panelPositionLabels(template.panels.length);

    return (
        <Stack
            gap={6}
            mt="sm"
            p="sm"
            style={{
                border: '1px solid var(--mantine-color-default-border)',
                borderRadius: 8,
                background: 'var(--mantine-color-default-hover)',
            }}
        >
            <Text size="xs" c="dimmed">
                Panels fill a {SCREEN_WIDTH_PX} × {SCREEN_HEIGHT_PX} portrait screen. Build artwork at
                these sizes:
            </Text>
            {template.panels.map((heightPercent, index) => (
                <Group key={index} gap="xs" wrap="nowrap">
                    <Box
                        style={{
                            width: 8,
                            height: 8,
                            borderRadius: 2,
                            flexShrink: 0,
                            background:
                                index % 2 === 0
                                    ? 'var(--mantine-color-blue-6)'
                                    : 'var(--mantine-color-violet-6)',
                        }}
                    />
                    <Text size="xs" fw={500} style={{ minWidth: 64 }}>
                        {positions[index]}
                    </Text>
                    <Text size="xs" c="dimmed">
                        {panelSizeLabel(heightPercent)} px ({heightPercent}%)
                    </Text>
                </Group>
            ))}
        </Stack>
    );
};

export default SceneTemplateHint;
